import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'

import { ApiError, api } from '@/lib/api'
import type { DeviceSession, Role, SessionResponse, User } from '@/lib/types'

export const sessionKey = ['session'] as const

/**
 * Sesi dibaca dari server, bukan dari penyimpanan lokal.
 *
 * Token sesi berada pada cookie HttpOnly yang tidak dapat dibaca JavaScript;
 * satu-satunya cara mengetahui siapa yang sedang masuk adalah bertanya ke
 * server. Balasan 401 bukan galat melainkan jawaban sah "belum masuk".
 */
export async function fetchSession(): Promise<SessionResponse | null> {
  try {
    return await api.get<SessionResponse>('/auth/me')
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized) return null
    throw error
  }
}

export const sessionQueryOptions = {
  queryKey: sessionKey,
  queryFn: fetchSession,
  staleTime: 60_000,
  retry: false,
}

export function useSession() {
  return useQuery(sessionQueryOptions)
}

/** ensureSession dipakai penjaga rute sebelum halaman dirender. */
export function ensureSession(client: QueryClient): Promise<SessionResponse | null> {
  return client.ensureQueryData(sessionQueryOptions)
}

/**
 * readSessionAfterLogin memastikan peramban benar-benar menyimpan cookie sesi.
 *
 * Cookie sesi bersifat HttpOnly, jadi tidak ada cara memeriksanya dari sisi
 * klien selain bertanya balik ke server.
 *
 * Percobaannya diulang beberapa kali sebelum menyerah. Pada jaringan seluler,
 * satu permintaan yang jatuh atau satu koneksi yang sedang berpindah sudah
 * cukup membuat pemeriksaan pertama gagal — dan menyimpulkan "gagal masuk"
 * dari satu permintaan yang meleset adalah kesimpulan yang terlalu cepat.
 * Hanya bila seluruh percobaan menjawab sama, sesinya memang tidak ada.
 */
export async function readSessionAfterLogin(client: QueryClient): Promise<SessionResponse | null> {
  const jeda = [0, 250, 700]

  for (const tunggu of jeda) {
    if (tunggu > 0) await new Promise((resolve) => setTimeout(resolve, tunggu))

    try {
      // Cache dibuang lebih dulu supaya percobaan berikutnya benar-benar
      // bertanya ke server, bukan membaca ulang jawaban kosong yang sudah
      // terlanjur tersimpan.
      client.removeQueries({ queryKey: sessionKey })
      const session = await client.fetchQuery(sessionQueryOptions)
      if (session) return session
    } catch {
      // Galat jaringan diperlakukan seperti jawaban kosong: dicoba lagi.
    }
  }

  return null
}

export interface LoginInput {
  email: string
  password: string
}

export function useLogin() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: LoginInput) => api.post<{ user: User; expires_at: string }>('/auth/login', input),
    onSuccess: async () => {
      // Seluruh cache dibuang saat identitas berganti: data milik pengguna
      // sebelumnya tidak boleh tersisa di layar pengguna berikutnya.
      await client.invalidateQueries()
    },
  })
}

export interface RegisterInput {
  email: string
  password: string
  full_name: string
  phone?: string
  roles?: Role[]
}

export function useRegister() {
  return useMutation({
    mutationFn: (input: RegisterInput) => api.post<{ user: User }>('/auth/register', input),
  })
}

export function useLogout() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSuccess: () => {
      client.clear()
    },
  })
}

export function useDeviceSessions() {
  return useQuery({
    queryKey: ['auth', 'sessions'],
    queryFn: () => api.get<{ sessions: DeviceSession[] }>('/auth/sessions'),
  })
}

export function useRevokeSession() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (sessionId: string) => api.delete<void>(`/auth/sessions/${sessionId}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['auth', 'sessions'] }),
  })
}

/** hasRole memeriksa peran tanpa memanggil hook, dipakai di penjaga rute. */
export function hasRole(session: SessionResponse | null, ...roles: Role[]): boolean {
  if (!session) return false
  return roles.some((role) => session.user.roles.includes(role))
}

export function hasPermission(session: SessionResponse | null, permission: string): boolean {
  return session?.permissions.includes(permission) ?? false
}

/**
 * useUploadAvatar mengunggah foto profil.
 *
 * Dikirim sebagai multipart, bukan JSON ber-base64: base64 membengkakkan
 * muatan sepertiga dan memaksa seluruh berkas berada di memori dua kali —
 * sekali sebagai teks, sekali sesudah diurai.
 */
export function useUploadAvatar() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData()
      body.append('file', file)

      // Content-Type sengaja tidak disetel: peramban yang menuliskannya,
      // lengkap dengan batas multipart yang tidak dapat ditebak sendiri.
      const response = await fetch('/api/v1/me/avatar', {
        method: 'POST',
        credentials: 'include',
        body,
      })

      const payload = await response.json().catch(() => null)
      if (!response.ok) {
        throw new ApiError(
          response.status,
          payload?.error?.code ?? 'UPLOAD_FAILED',
          payload?.error?.message ?? 'Foto gagal diunggah.',
        )
      }
      return payload as { avatar_url: string }
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: sessionKey })
    },
  })
}

export function useRemoveAvatar() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: () => api.delete<void>('/me/avatar'),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: sessionKey })
    },
  })
}

/**
 * useForgotPassword meminta tautan atur ulang kata sandi.
 *
 * Peladen selalu menjawab sama, ada atau tidak ada akun dengan alamat itu,
 * sehingga antarmuka pun tidak boleh membedakan keduanya — pesan sukses yang
 * hanya muncul untuk alamat terdaftar akan membocorkan apa yang sengaja
 * disembunyikan peladen.
 */
export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => api.post<{ message: string }>('/auth/forgot-password', { email }),
  })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (input: { token: string; password: string }) =>
      api.post<{ message: string }>('/auth/reset-password', input),
  })
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) => api.post<{ message: string }>('/auth/verify-email', { token }),
  })
}

export function useSendVerification() {
  return useMutation({
    mutationFn: () => api.post<{ message: string }>('/auth/send-verification'),
  })
}

export function useChangePassword() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { current_password: string; new_password: string }) =>
      api.post<{ message: string }>('/auth/change-password', input),
    onSuccess: async () => {
      // Seluruh sesi dicabut peladen, termasuk milik pemanggil. Cache
      // dikosongkan supaya antarmuka tidak menampilkan data milik sesi yang
      // sudah tidak berlaku.
      client.clear()
    },
  })
}
